import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { 
  ArrowRight, 
  Lightbulb, 
  Zap, 
  Bot, 
  Users, 
  BarChart3,
  CheckCircle,
  Play,
  MessageSquare,
  FileText,
  Settings,
  TrendingUp,
  Target
} from 'lucide-react'

export function LandingPage() {
  const navigate = useNavigate()

  const handleStartJourney = () => {
    navigate('/onboarding')
  }

  const stages = [
    {
      id: 0,
      title: "AI Landscape Overview",
      subtitle: "Professional foundation",
      description: "15-minute educational presentation covering the complete AI ecosystem",
      icon: Lightbulb,
      color: "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/20"
    },
    {
      id: 1,
      title: "AI Chat & Tools",
      subtitle: "Creative partnership",
      description: "Hands-on experience with AI-powered content creation and ideation",
      icon: MessageSquare,
      color: "text-blue-600 bg-blue-100 dark:bg-blue-900/20"
    },
    {
      id: 2,
      title: "Collaborative Creation",
      subtitle: "Team productivity",
      description: "Multi-stage content development with AI as your creative partner",
      icon: FileText,
      color: "text-green-600 bg-green-100 dark:bg-green-900/20"
    },
    {
      id: 3,
      title: "Workflow Automation",
      subtitle: "Efficiency multiplier",
      description: "Visual automation playground for streamlining repetitive tasks",
      icon: Settings,
      color: "text-purple-600 bg-purple-100 dark:bg-purple-900/20"
    },
    {
      id: 4,
      title: "Business Intelligence",
      subtitle: "Data-driven insights",
      description: "Advanced analytics and decision-making with AI-powered analysis",
      icon: BarChart3,
      color: "text-indigo-600 bg-indigo-100 dark:bg-indigo-900/20"
    },
    {
      id: 5,
      title: "AI Agents & Automation",
      subtitle: "Autonomous systems",
      description: "Personalized recommendation engine and intelligent automation",
      icon: Bot,
      color: "text-orange-600 bg-orange-100 dark:bg-orange-900/20"
    }
  ]

  const benefits = [
    {
      icon: Target,
      title: "Hands-On Learning",
      description: "Interactive experiences, not just theory"
    },
    {
      icon: TrendingUp,
      title: "Progressive Skill Building",
      description: "From AI curious to AI capable in structured stages"
    },
    {
      icon: Users,
      title: "Real Business Applications",
      description: "Practical tools you can implement immediately"
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md dark:bg-gray-900/80">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-white shadow-sm">
                <img 
                  src="/ekow-logo.png" 
                  alt="Ekow Logo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">AI Journey</h1>
                <p className="text-sm text-muted-foreground">From Curious to Capable</p>
              </div>
            </div>
            <Button onClick={handleStartJourney} className="gap-2">
              Start Journey <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center space-y-8 mb-16">
          <div className="space-y-4">
            <h1 className="text-5xl md:text-6xl font-bold text-foreground">
              Master AI for Your
              <span className="text-primary block">Business Success</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              A comprehensive, hands-on journey from AI curious to AI capable. Learn through 
              interactive experiences designed for real business applications.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/overview')}
              className="gap-2 text-lg px-8 py-4 bg-yellow-600 hover:bg-yellow-700"
            >
              <Lightbulb className="w-5 h-5" />
              Quick Overview (15 min)
            </Button>
            <Button 
              size="lg" 
              onClick={handleStartJourney}
              className="gap-2 text-lg px-8 py-4"
            >
              <Play className="w-5 h-5" />
              Start Full Journey
            </Button>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center text-foreground mb-8">
            Why This Journey Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="text-center">
                <CardContent className="pt-6">
                  <div className="p-3 bg-primary/10 rounded-full w-fit mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Journey Stages */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Your Complete AI Learning Path
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Six progressive stages that transform you from AI curious to AI capable, 
              with hands-on tools you can use immediately.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stages.map((stage, index) => (
              <Card key={stage.id} className="relative overflow-hidden group hover-lift">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`p-2 rounded-lg ${stage.color}`}>
                      <stage.icon className="w-6 h-6" />
                    </div>
                    <div className="px-2 py-1 bg-muted rounded-md text-xs font-medium">
                      Stage {stage.id}
                    </div>
                  </div>
                  <CardTitle className="text-lg">{stage.title}</CardTitle>
                  <CardDescription className="font-medium text-primary">
                    {stage.subtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{stage.description}</p>
                </CardContent>
                
                {/* Progress indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted">
                  <div 
                    className="h-full bg-primary transition-all duration-500 group-hover:bg-primary/80"
                    style={{ width: `${((index + 1) / stages.length) * 100}%` }}
                  />
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <Card className="max-w-2xl mx-auto bg-primary/5 border-primary/20">
            <CardContent className="pt-8 pb-8">
              <h3 className="text-2xl font-bold text-foreground mb-4">
                Ready to Transform Your Business with AI?
              </h3>
              <p className="text-muted-foreground mb-6">
                Join thousands of business leaders who've completed this journey. 
                Start with a 15-minute overview and progress at your own pace.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg"
                  onClick={() => navigate('/overview')}
                  className="gap-2 text-lg px-8 bg-yellow-600 hover:bg-yellow-700"
                >
                  <Lightbulb className="w-5 h-5" />
                  Quick Overview
                </Button>
                <Button 
                  size="lg"
                  onClick={handleStartJourney}
                  className="gap-2 text-lg px-8"
                >
                  <CheckCircle className="w-5 h-5" />
                  Full Journey
                </Button>
              </div>
              <div className="text-sm text-muted-foreground flex items-center justify-center gap-2 mt-4">
                <Zap className="w-4 h-4" />
                Overview: 15 minutes • Full Journey: 2-4 hours • Immediately applicable
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  )
}